/**
 * User Management Page
 *
 * Admin-gated page for managing MediaSim users. Lists everyone who has logged
 * in (upserted into the mediasim_users table) and lets an admin promote/demote
 * between standard and admin roles. Access is gated on the DB-backed role, not
 * a Cognito group claim.
 */

import { useMemo, useState } from 'react';
import { Button, Card, Flex, Input, Stack, Table, useTheme, spacing } from '@fti/design-system';
import { useAllUsers, useUpdateUserRole } from '../../api/media-sim/users';
import { useMediaSimUser } from '../../components/media-sim/layout/MediaSimUserContext';
import type { MediaSimRole, MediaSimUserRecord } from '../../types/media-sim';

export function UserManagementPage() {
  const { theme } = useTheme();
  const { isAdmin, loaded, record: currentUser, refresh } = useMediaSimUser();

  const { data: users, loading, error, refetch } = useAllUsers();
  const { updateRole } = useUpdateUserRole();

  const [search, setSearch] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const errorColor = theme.colors.semantic.error.default;

  const filtered = useMemo(() => {
    const list = users ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      (u) =>
        u.email.toLowerCase().includes(term) ||
        (u.name ?? '').toLowerCase().includes(term)
    );
  }, [users, search]);

  // Gate on DB role. While the role is still loading, don't flash "access denied".
  if (loaded && !isAdmin) {
    return (
      <Stack gap="lg">
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: textColor, margin: 0 }}>
          User Management
        </h1>
        <Card variant="outlined">
          <Card.Body>
            <Flex justify="center" style={{ padding: spacing.lg }}>
              <span style={{ color: errorColor }}>Access denied. Admin role required.</span>
            </Flex>
          </Card.Body>
        </Card>
      </Stack>
    );
  }

  const handleToggleRole = async (u: MediaSimUserRecord) => {
    setActionError(null);
    const nextRole: MediaSimRole = u.role === 'admin' ? 'standard' : 'admin';
    setPendingId(u.id);
    try {
      await updateRole(u.id, nextRole);
      refetch();
      // If the admin changed their own role, refresh the shared context so the
      // nav/gating update immediately.
      if (currentUser && u.id === currentUser.id) refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update role.');
    } finally {
      setPendingId(null);
    }
  };

  const columns = [
    { key: 'name', header: 'Name', render: (_v: unknown, row: Record<string, unknown>) => (
      <span style={{ color: textColor }}>{(row as unknown as MediaSimUserRecord).name || '—'}</span>
    ) },
    { key: 'email', header: 'Email' },
    {
      key: 'role',
      header: 'Role',
      render: (_v: unknown, row: Record<string, unknown>) => {
        const u = row as unknown as MediaSimUserRecord;
        const isSelf = currentUser?.id === u.id;
        return (
          <span
            style={{
              fontWeight: u.role === 'admin' ? 600 : 400,
              color: u.role === 'admin' ? theme.colors.primary[600] : mutedColor,
            }}
          >
            {u.role}
            {isSelf ? ' (you)' : ''}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_v: unknown, row: Record<string, unknown>) => {
        const u = row as unknown as MediaSimUserRecord;
        return (
          <Button
            variant="outline"
            size="sm"
            color={u.role === 'admin' ? 'error' : 'primary'}
            disabled={pendingId === u.id}
            onClick={() => handleToggleRole(u)}
          >
            {pendingId === u.id
              ? 'Saving…'
              : u.role === 'admin'
              ? 'Make standard'
              : 'Make admin'}
          </Button>
        );
      },
    },
  ];

  return (
    <Stack gap="lg">
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: textColor, margin: 0 }}>
          User Management
        </h1>
        <p style={{ margin: 0, marginTop: spacing.xs, fontSize: '0.9rem', color: mutedColor }}>
          Manage MediaSim users and roles. Users appear here after their first sign-in.
        </p>
      </div>

      <Input
        placeholder="Filter by name or email..."
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
      />

      {(error || actionError) && (
        <Card variant="outlined">
          <Card.Body>
            <span style={{ color: errorColor }}>
              {actionError || `Failed to load users: ${error?.message}`}
            </span>
          </Card.Body>
        </Card>
      )}

      {loading && (
        <Card variant="outlined">
          <Card.Body>
            <Flex justify="center" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor }}>Loading users…</span>
            </Flex>
          </Card.Body>
        </Card>
      )}

      {!loading && !error && (
        filtered.length > 0 ? (
          <Table data={filtered as unknown as Record<string, unknown>[]} columns={columns} />
        ) : (
          <Card variant="outlined">
            <Card.Body>
              <Flex justify="center" style={{ padding: spacing.lg }}>
                <span style={{ color: mutedColor }}>
                  {search.trim() ? 'No matching users.' : 'No users found.'}
                </span>
              </Flex>
            </Card.Body>
          </Card>
        )
      )}
    </Stack>
  );
}
